import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { schedulingApi, type AppointmentStatus, type AppointmentType, type PaginatedAppointments } from '@/api';

const APPOINTMENTS_KEY = 'appointments';

interface UseAppointmentsParams {
  status?: AppointmentStatus[];
  type?: AppointmentType[];
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

async function fetchPatientAppointments(params?: UseAppointmentsParams): Promise<PaginatedAppointments> {
  return schedulingApi.getPatientAppointments(params);
}

export function useAppointments(params?: UseAppointmentsParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [APPOINTMENTS_KEY, 'patient', params],
    queryFn: () => fetchPatientAppointments(params),
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 10,
    retry: 2,
    enabled: options?.enabled !== false,
  });
}

export function useUpcomingAppointments(limit = 10) {
  return useQuery({
    queryKey: [APPOINTMENTS_KEY, 'upcoming', limit],
    queryFn: () => schedulingApi.getUpcomingAppointments(limit),
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
    retry: 2,
  });
}

export function useAppointmentHistory(params?: { limit?: number; offset?: number }) {
  return useQuery({
    queryKey: [APPOINTMENTS_KEY, 'history', params],
    queryFn: () => schedulingApi.getAppointmentHistory(params),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    retry: 2,
  });
}

export function useDoctorAppointments(
  doctorId: string,
  params?: {
    status?: AppointmentStatus[];
    date?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }
) {
  return useQuery({
    queryKey: [APPOINTMENTS_KEY, 'doctor', doctorId, params],
    queryFn: () => schedulingApi.getDoctorAppointments(doctorId, params),
    enabled: !!doctorId,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 10,
    retry: 2,
  });
}

export function useMyAppointments(
  params?: {
    status?: AppointmentStatus[];
    date?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  },
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [APPOINTMENTS_KEY, 'me', params],
    queryFn: () => schedulingApi.getMyAppointments(params),
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 10,
    retry: 2,
    enabled: options?.enabled !== false,
  });
}

export function useUpdateAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appointmentId,
      data,
    }: {
      appointmentId: string;
      data: { reason?: string; notes?: string; internalNotes?: string };
    }) => schedulingApi.updateAppointment(appointmentId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [APPOINTMENTS_KEY] });
      queryClient.setQueryData([APPOINTMENTS_KEY, 'appointment', data.id], data);
    },
  });
}

export function useConfirmAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (appointmentId: string) => schedulingApi.confirmAppointment(appointmentId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [APPOINTMENTS_KEY] });
      queryClient.setQueryData([APPOINTMENTS_KEY, 'appointment', data.id], (old: unknown) => ({
        ...(old as object),
        status: data.status,
        confirmedAt: data.confirmedAt,
      }));
    },
  });
}

export function useStartAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (appointmentId: string) => schedulingApi.startAppointment(appointmentId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [APPOINTMENTS_KEY] });
      queryClient.setQueryData([APPOINTMENTS_KEY, 'appointment', data.id], (old: unknown) => ({
        ...(old as object),
        status: data.status,
        startedAt: data.startedAt,
      }));
    },
  });
}

export function useFinishAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appointmentId,
      data,
    }: {
      appointmentId: string;
      data: { duration?: number; notes?: string };
    }) => schedulingApi.finishAppointment(appointmentId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [APPOINTMENTS_KEY] });
      queryClient.setQueryData([APPOINTMENTS_KEY, 'appointment', data.id], (old: unknown) => ({
        ...(old as object),
        status: data.status,
        finishedAt: data.finishedAt,
        duration: data.duration,
        medicalRecordId: data.medicalRecordId,
      }));
    },
  });
}
